import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { cpoScope, session } from './session'

/**
 * The backend's live channel, /ws/notifications (not in its OpenAPI file).
 * Messages are {event, data}. Operators receive session_start/update/stop,
 * charger_status, charger_boot, remote_start, station_update and
 * pricing_plan_update. A message refreshes the affected screens; bursts
 * (MeterValues every few seconds) are coalesced.
 */

const MOCK = import.meta.env.VITE_MOCK === 'true'

export type LiveState = 'connecting' | 'live' | 'offline'

interface LiveCtx {
  state: LiveState
  lastEventAt: number | null
  subscribe: (fn: (event: string, data: Record<string, unknown>) => void) => () => void
}

const Ctx = createContext<LiveCtx>({ state: 'offline', lastEventAt: null, subscribe: () => () => undefined })

const INVALIDATES: Record<string, string[][]> = {
  session_start: [['sessions'], ['chargers'], ['analytics'], ['live']],
  session_update: [['live']],
  session_stop: [['sessions'], ['chargers'], ['analytics'], ['live'], ['credits']],
  charger_status: [['chargers'], ['live']],
  charger_boot: [['chargers'], ['live'], ['logs']],
  connector_status: [['chargers'], ['live']],
  remote_start: [['live'], ['logs']],
  station_update: [['stations']],
  pricing_plan_update: [['pricing']],
  wallet_update: [['customers'], ['credits']],
  cpo_user_wallet: [['customers'], ['credits']],
  sa_global_wallet: [['admin', 'users'], ['admin', 'transactions'], ['admin', 'super-cards'], ['admin', 'settlements']],
}

export function RealtimeProvider({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const qc = useQueryClient()
  const [state, setState] = useState<LiveState>('offline')
  const [lastEventAt, setLastEventAt] = useState<number | null>(null)
  const listeners = useRef(new Set<(e: string, d: Record<string, unknown>) => void>())
  const [scopeVersion, setScopeVersion] = useState(0)

  useEffect(() => cpoScope.subscribe(() => setScopeVersion((v) => v + 1)), [])
  useEffect(() => session.subscribe(() => setScopeVersion((v) => v + 1)), [])

  useEffect(() => {
    if (!enabled || MOCK) return
    let ws: WebSocket | null = null
    let retry = 0
    let stopped = false
    let timer: number | undefined
    const original = session.get()
    const scopeVersion = cpoScope.version()
    const isCurrent = () => !stopped && session.get() === original && cpoScope.version() === scopeVersion
    const pending = new Set<string>()
    let flushTimer: number | null = null

    const flush = () => {
      flushTimer = null
      if (!isCurrent()) { pending.clear(); return }
      pending.forEach((k) => qc.invalidateQueries({ queryKey: JSON.parse(k) }))
      pending.clear()
    }

    const connect = () => {
      const s = session.get()
      if (!s || stopped) return
      setState('connecting')
      const params = new URLSearchParams({ token: s.accessToken })
      const scope = cpoScope.get()
      if (s.user.role === 'superadmin' && scope) params.set('monitor_cpo_id', String(scope))
      const proto = location.protocol === 'https:' ? 'wss' : 'ws'
      ws = new WebSocket(`${proto}://${location.host}/api/ws/notifications?${params}`)
      ws.onopen = () => {
        if (!isCurrent()) return
        retry = 0
        setState('live')
      }
      ws.onmessage = (msg) => {
        if (!isCurrent()) return
        if (msg.data === 'pong') return
        try {
          const { event, data } = JSON.parse(msg.data) as { event: string; data: Record<string, unknown> }
          if (!event) return
          setLastEventAt(Date.now())
          listeners.current.forEach((l) => l(event, data ?? {}))
          ;(INVALIDATES[event] ?? []).forEach((key) => pending.add(JSON.stringify(key)))
          flushTimer ??= window.setTimeout(flush, 800)
        } catch {
          /* not JSON */
        }
      }
      ws.onclose = () => {
        if (!isCurrent()) return
        setState('offline')
        if (stopped) return
        // The access token may have expired; the API client refreshes it on
        // the next REST call, and we retry with backoff.
        retry = Math.min(retry + 1, 6)
        timer = window.setTimeout(connect, 1000 * 2 ** retry)
      }
    }

    connect()
    const ping = window.setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 25000)
    return () => {
      stopped = true
      window.clearInterval(ping)
      window.clearTimeout(timer)
      if (flushTimer !== null) window.clearTimeout(flushTimer)
      pending.clear()
      setLastEventAt(null)
      setState('offline')
      ws?.close()
    }
  }, [enabled, qc, scopeVersion])

  const subscribe = useCallback<LiveCtx['subscribe']>((fn) => {
    listeners.current.add(fn)
    return () => {
      listeners.current.delete(fn)
    }
  }, [])
  // Mock mode has no socket; a signed-out shell shows offline.
  const shown: LiveState = MOCK ? 'live' : enabled ? state : 'offline'
  const value = useMemo<LiveCtx>(() => ({ state: shown, lastEventAt, subscribe }), [shown, lastEventAt, subscribe])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useLive = () => useContext(Ctx)
